import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Check,
  Filter,
  Minus,
  PackageOpen,
  Plus,
  Search,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";

import { getCollections, CollectionDto } from "../api/collectionApi";
import {
  getProductClientById,
  getClientProductList,
  ProductDetailDto,
  ProductListDto,
  FacetDto,
} from "../api/productApi";

import { useCart } from "../contexts/CartProvider";
import SafeImg from "../components/common/SafeImg";
import { gaViewItem, gaAddToCart } from "../analytics/ga";

/* ======================
   HELPERS
====================== */
const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");
const FALLBACK_IMG = "/images/placeholder.jpg";

function resolveImg(url?: string | null) {
  if (!url) return FALLBACK_IMG;
  const u = String(url).split("\\").join("/").trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return API_URL ? `${API_URL}${u}` : u;
  return API_URL ? `${API_URL}/${u}` : u;
}

function isDesktopHover() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches
  );
}

function formatVND(v: number) {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(v ?? 0);
}

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return v;
}

function normalizeCollection(c: any): CollectionDto {
  return {
    ...c,
    id: Number(c?.id ?? c?.collectionId ?? 0),
    slug: String(c?.slug ?? ""),
    name: String(c?.name ?? ""),
  } as CollectionDto;
}

function normalizeProduct(p: any): ProductListDto {
  const id = Number(p?.id ?? p?.productId ?? p?.ProductId ?? 0);

  const basePrice = Number(p?.price ?? p?.Price ?? p?.basePrice ?? 0);
  const salePrice =
    p?.finalPrice != null
      ? Number(p.finalPrice)
      : p?.FinalPrice != null
        ? Number(p.FinalPrice)
        : p?.salePrice != null
          ? Number(p.salePrice)
          : null;

  const imageUrls: any[] =
    (Array.isArray(p?.imageUrls) ? p.imageUrls : null) ??
    (Array.isArray(p?.ImageUrls) ? p.ImageUrls : null) ??
    [];

  const thumbnail =
    p?.thumbnail ??
    p?.thumbnailUrl ??
    p?.imageUrl ??
    (imageUrls.length ? imageUrls[0] : null) ??
    (Array.isArray(p?.images) ? p.images?.[0]?.imageUrl : null) ??
    null;

  const name = String(p?.name ?? p?.Name ?? "");
  const sku = String(p?.sku ?? p?.SKU ?? p?.Sku ?? "").trim();
  const slug = String(p?.slug ?? (sku.length ? sku : String(id)));

  return {
    id,
    name,
    slug,
    basePrice,
    salePrice,
    thumbnail,
  };
}

/* ======================
   UI
====================== */
function FilterChip(props: {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "inline-flex h-10 items-center rounded-full border px-4 text-[10px] font-medium uppercase tracking-[0.16em] transition",
        props.active
          ? "border-brand-brown bg-brand-brown text-brand-ivory shadow-[0_14px_34px_rgba(43,31,26,0.14)]"
          : "border-brand-brown/12 bg-white/75 text-brand-brown/70 hover:border-brand-brown/25 hover:bg-white",
        props.className,
      )}
    >
      {props.children}
    </button>
  );
}

function SelectField(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        "h-11 rounded-full border border-brand-brown/12 bg-white/80 px-4 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/76 outline-none transition hover:border-brand-brown/26 focus:border-brand-brown/34",
        props.className,
      )}
    />
  );
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[28px] border border-brand-brown/10 bg-white/75 shadow-[0_18px_50px_rgba(43,31,26,0.05)]">
      <div className="aspect-[4/5] animate-pulse bg-brand-brown/6" />
      <div className="space-y-3 p-4 sm:p-5">
        <div className="h-3 w-20 animate-pulse rounded bg-brand-brown/10" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-brand-brown/10" />
        <div className="h-4 w-24 animate-pulse rounded bg-brand-brown/10" />
      </div>
    </div>
  );
}

function EmptyState(props: {
  title: string;
  desc: string;
  action: string;
  onReset: () => void;
}) {
  return (
    <div className="rounded-[30px] border border-brand-brown/10 bg-white/78 p-10 text-center shadow-[0_24px_80px_rgba(43,31,26,0.06)]">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-brown/6 text-brand-brown">
        <PackageOpen className="h-5 w-5" />
      </div>
      <h3 className="mt-5 text-[24px] font-medium text-brand-brown sm:text-[30px]">
        {props.title}
      </h3>
      <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-brand-brown/58">
        {props.desc}
      </p>
      <button
        type="button"
        onClick={props.onReset}
        className="mt-6 inline-flex h-12 items-center gap-2 rounded-full bg-brand-brown px-6 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-ivory transition hover:opacity-90"
      >
        {props.action}
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function ProductCard(props: {
  p: ProductListDto;
  label: string;
  dense: boolean;
  desktop: boolean;
  text: {
    sale: string;
    viewProduct: string;
    quickView: string;
  };
  go: (to: string) => void;
  openQuickFor: (id: number) => void;
}) {
  const { p, label, dense, desktop, text, go, openQuickFor } = props;

  const id = Number((p as any).id ?? 0);
  const basePrice = Number((p as any).basePrice ?? 0);
  const salePrice = (p as any).salePrice;
  const price = typeof salePrice === "number" ? salePrice : basePrice;
  const hasSale =
    typeof salePrice === "number" && salePrice > 0 && salePrice < basePrice;

  return (
    <article className="group">
      <div className="overflow-hidden rounded-[28px] border border-brand-brown/10 bg-white/78 shadow-[0_20px_60px_rgba(43,31,26,0.06)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_28px_90px_rgba(43,31,26,0.1)]">
        <div className="relative">
          <button onClick={() => go(`/product/${id}`)} className="block w-full text-left">
            <div className="aspect-[4/5] overflow-hidden bg-[#efe8de]">
              <SafeImg
                src={resolveImg((p as any).thumbnail)}
                alt={(p as any).name}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
          </button>

          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-white/15 bg-white/84 px-3 py-1.5 text-[9px] font-medium uppercase tracking-[0.16em] text-brand-brown/72 backdrop-blur-md">
                {label}
              </span>

              {hasSale && (
                <span className="rounded-full bg-brand-brown px-3 py-1.5 text-[9px] font-medium uppercase tracking-[0.16em] text-brand-ivory">
                  {text.sale}
                </span>
              )}
            </div>

            <button
              onClick={() => openQuickFor(id)}
              className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-[#231a18]/88 text-brand-ivory backdrop-blur-md transition hover:bg-brand-brown",
                desktop ? "opacity-0 group-hover:opacity-100" : "opacity-100",
              )}
              aria-label={text.quickView}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className={cn("space-y-4 p-4 sm:p-5", dense && "p-4")}>
          <div className="space-y-2">
            <h3 className="line-clamp-2 text-[15px] font-medium leading-6 text-brand-brown sm:text-[16px]">
              {(p as any).name}
            </h3>

            <div className="flex items-end gap-3">
              <span className="text-[15px] font-medium text-brand-brown sm:text-base">
                {formatVND(price)}
              </span>
              {hasSale && (
                <span className="text-[11px] text-brand-brown/40 line-through">
                  {formatVND(basePrice)}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-2 border-t border-brand-brown/8 pt-4">
            <button
              onClick={() => go(`/product/${id}`)}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-brand-brown/12 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:border-brand-brown hover:bg-brand-brown hover:text-brand-ivory"
            >
              {text.viewProduct}
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={() => openQuickFor(id)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#231a18] text-brand-ivory transition hover:opacity-90"
              aria-label={text.quickView}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ======================
   PAGE
====================== */
export default function Collection() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const desktop = isDesktopHover();
  const { addItem, open: openCart } = useCart();

  const isVN = i18n.language?.startsWith("vi");
  const text = {
    heroKicker: "OWL DREAM / COLLECTION",
    heroSubtitle: isVN ? "Sản phẩm là trọng tâm." : "Product comes first.",
    allPieces: isVN ? "Tất cả sản phẩm" : "All pieces",
    pieces: isVN ? "sản phẩm" : "pieces",
    filters: isVN ? "bộ lọc" : "filters",
    localBrand: isVN ? "Local brand store" : "Local brand store",
    searchPlaceholder: isVN ? "Tìm sản phẩm" : "Search piece",
    category: isVN ? "Danh mục" : "Category",
    color: isVN ? "Màu" : "Color",
    size: isVN ? "Size" : "Size",
    inStock: isVN ? "Còn hàng" : "In stock",
    newest: isVN ? "Mới nhất" : "Newest",
    oldest: isVN ? "Cũ nhất" : "Oldest",
    priceAsc: isVN ? "Giá ↑" : "Price ↑",
    priceDesc: isVN ? "Giá ↓" : "Price ↓",
    dense: isVN ? "Dày" : "Dense",
    comfort: isVN ? "Thoáng" : "Comfort",
    reset: isVN ? "Đặt lại" : "Reset",
    productGrid: isVN ? "Lưới sản phẩm" : "Product grid",
    noProducts: isVN ? "Không có sản phẩm" : "No products found",
    noProductsDescSearch: (q: string) =>
      isVN ? `Không có kết quả cho “${q}”.` : `No result for “${q}”.`,
    noProductsDescCollection: (name: string) =>
      isVN
        ? `${name} hiện chưa có sản phẩm hiển thị.`
        : `${name} has no visible products yet.`,
    resetFilters: isVN ? "Xoá bộ lọc" : "Reset filters",
    loading: isVN ? "Đang tải" : "Loading",
    loadingMore: isVN ? "Đang tải thêm" : "Loading more",
    sale: isVN ? "Giảm giá" : "Sale",
    viewProduct: isVN ? "Xem sản phẩm" : "View product",
    quickView: isVN ? "Xem nhanh" : "Quick view",
    selected: isVN ? "Đã chọn" : "Selected",
    chooseVariant: isVN ? "Chọn màu + size để thêm nhanh." : "Choose color + size to add fast.",
    fullPage: isVN ? "Trang chi tiết" : "Full page",
    addToCart: isVN ? "Thêm vào giỏ" : "Add to cart",
    adding: isVN ? "Đang thêm..." : "Adding...",
    quantity: isVN ? "Số lượng" : "Quantity",
    stock: isVN ? "Tồn kho" : "Stock",
    close: isVN ? "Đóng" : "Close",
    search: isVN ? "Tìm kiếm" : "Search",
    searchResult: isVN ? "Kết quả tìm kiếm" : "Search result",
    chooseColorFirst: isVN ? "Chọn màu trước để hiện size." : "Choose a color first.",
    noSize: isVN ? "Màu này hiện không còn size." : "This color has no available size.",
    noStockVariants: isVN
      ? "Hiện không còn biến thể còn hàng."
      : "No available variants in stock.",
    currentCollection: isVN ? "Bộ sưu tập hiện tại" : "Current collection",
  };

  const [collections, setCollections] = useState<CollectionDto[]>([]);
  const [activeCollectionSlug, setActiveCollectionSlug] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 350);

  const [category, setCategory] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [size, setSize] = useState<string | null>(null);
  const [inStockOnly, setInStockOnly] = useState(false);

  const [sort, setSort] = useState<"new" | "old" | "priceAsc" | "priceDesc">("new");
  const [dense, setDense] = useState(false);

  const [items, setItems] = useState<ProductListDto[]>([]);
  const [facets, setFacets] = useState<FacetDto>({
    colors: [],
    sizes: [],
    categories: [],
  });

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const PAGE_SIZE = 12;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const [openQuickId, setOpenQuickId] = useState<number | null>(null);
  const [detailCache, setDetailCache] = useState<Record<number, ProductDetailDto>>({});
  const [detailLoading, setDetailLoading] = useState<Record<number, boolean>>({});

  const [qColor, setQColor] = useState<string | null>(null);
  const [qSize, setQSize] = useState<string | null>(null);
  const [qQty, setQQty] = useState(1);
  const [addingQuick, setAddingQuick] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const res = await getCollections({
          page: 1,
          pageSize: 100,
          sort: "order",
        } as any);

        if (!alive) return;

        const list = Array.isArray(res) ? res.map(normalizeCollection) : [];
        setCollections(list);

        if (list.length > 0) {
          setActiveCollectionSlug(list[0].slug);
        }
      } catch (e: any) {
        if (!alive) return;
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load collections";
        setError(String(msg));
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const collectionMap = useMemo(() => {
    const m: Record<string, string> = {};
    collections.forEach((c) => (m[c.slug] = c.name));
    return m;
  }, [collections]);

  const activeCollectionName = activeCollectionSlug
    ? collectionMap[activeCollectionSlug] || text.allPieces
    : text.allPieces;

  const reqIdRef = useRef(0);

  const mergeFacet = (list: string[], selected: string | null) => {
    const arr = Array.isArray(list) ? [...list] : [];
    if (selected && !arr.includes(selected)) arr.unshift(selected);
    return arr;
  };

  const fetchList = async (p: number, append: boolean) => {
    const myId = ++reqIdRef.current;

    const qv = debouncedQ.trim() || undefined;
    const cat = category || undefined;
    const col = color || undefined;
    const sz = size || undefined;

    const listRes = await getClientProductList({
      page: p,
      pageSize: PAGE_SIZE,
      q: qv,
      category: cat,
      color: col,
      size: sz,
      sort,
      inStockOnly,
    });

    const facetRes = await getClientProductList({
      page: 1,
      pageSize: 1,
      q: qv,
      category: cat,
      sort,
      inStockOnly,
    });

    if (myId !== reqIdRef.current) return;

    const data = (listRes.items || []).map(normalizeProduct);

    setTotalPages(Number(listRes.pagination?.totalPages ?? 1) || 1);

    const nextFacets = facetRes.facets ?? {
      colors: [],
      sizes: [],
      categories: [],
    };

    setFacets({
      categories: mergeFacet(nextFacets.categories ?? [], category),
      colors: mergeFacet(nextFacets.colors ?? [], color),
      sizes: mergeFacet(nextFacets.sizes ?? [], size),
    });

    setItems((prev) => (append ? [...prev, ...data] : data));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);

    fetchList(1, false)
      .catch((e: any) => {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load products";
        if (!cancelled) setError(String(msg));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQ, category, color, size, sort, inStockOnly]);

  const pageRef = useRef(page);
  const totalRef = useRef(totalPages);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);

  useEffect(() => {
    pageRef.current = page;
    totalRef.current = totalPages;
    loadingRef.current = loading;
    loadingMoreRef.current = loadingMore;
  }, [page, totalPages, loading, loadingMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loadingRef.current || loadingMoreRef.current) return;
        if (pageRef.current >= totalRef.current) return;

        const next = pageRef.current + 1;
        setLoadingMore(true);
        try {
          await fetchList(next, true);
          setPage(next);
        } finally {
          setLoadingMore(false);
        }
      },
      { rootMargin: "800px 0px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (openQuickId == null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openQuickId]);

  const go = (to: string) => {
    const doc = document as any;
    if (doc.startViewTransition) doc.startViewTransition(() => navigate(to));
    else navigate(to);
  };

  const openQuickFor = async (productId: number) => {
    if (!productId) return;

    setOpenQuickId(productId);
    setQColor(null);
    setQSize(null);
    setQQty(1);

    if (detailCache[productId] || detailLoading[productId]) return;

    setDetailLoading((m) => ({ ...m, [productId]: true }));
    try {
      const d = await getProductClientById(productId);
      setDetailCache((m) => ({ ...m, [productId]: d }));

      const price = Number(d.salePrice ?? d.basePrice ?? 0);

      gaViewItem({
        currency: "VND",
        value: price,
        item: {
          item_id: String(d.id ?? productId),
          item_name: String(d.name ?? ""),
          price,
          item_brand: "OWL DREAM",
          item_category: "Product",
        },
      });
    } finally {
      setDetailLoading((m) => ({ ...m, [productId]: false }));
    }
  };

  const closeQuick = () => setOpenQuickId(null);

  const quickDetail = openQuickId != null ? detailCache[openQuickId] : undefined;
  const quickLoading = openQuickId != null ? detailLoading[openQuickId] : false;

  const quickColors = useMemo(() => {
    if (!quickDetail) return [];
    return Array.from(
      new Set(
        quickDetail.variants
          .filter((v) => v.stockQuantity > 0)
          .map((v) => v.color),
      ),
    );
  }, [quickDetail]);

  const quickSizes = useMemo(() => {
    if (!quickDetail || !qColor) return [];
    return Array.from(
      new Set(
        quickDetail.variants
          .filter((v) => v.stockQuantity > 0 && v.color === qColor)
          .map((v) => v.size),
      ),
    );
  }, [quickDetail, qColor]);

  const quickVariant = useMemo(() => {
    if (!quickDetail || !qColor || !qSize) return undefined;
    return quickDetail.variants.find((v) => v.color === qColor && v.size === qSize);
  }, [quickDetail, qColor, qSize]);

  const quickPrice = useMemo(() => {
    if (!quickDetail) return 0;
    return quickDetail.salePrice ?? quickDetail.basePrice;
  }, [quickDetail]);

  const quickStock = quickVariant?.stockQuantity;

  const handleQuickAdd = async () => {
    if (!quickVariant || !openQuickId || !qColor || !qSize || !quickDetail) return;

    try {
      setAddingQuick(true);

      await addItem({
        productId: openQuickId,
        quantity: qQty,
        color: qColor,
        size: qSize,
      });

      const price = Number(quickDetail.salePrice ?? quickDetail.basePrice ?? 0);

      gaAddToCart({
        currency: "VND",
        value: price * qQty,
        quantity: qQty,
        item: {
          item_id: String(quickDetail.id ?? openQuickId),
          item_name: String(quickDetail.name ?? ""),
          price,
          item_brand: "OWL DREAM",
          item_category: "Product",
          item_variant: `${qColor}-${qSize}`,
        },
      });

      openCart();
      closeQuick();
    } finally {
      setAddingQuick(false);
    }
  };

  const activeFilterCount =
    Number(!!category) +
    Number(!!color) +
    Number(!!size) +
    Number(inStockOnly) +
    Number(!!q.trim());

  const resultCountLabel = useMemo(() => {
    if (loading) return text.loading;
    return `${items.length.toString().padStart(2, "0")} ${text.pieces}`;
  }, [items.length, loading, text.loading, text.pieces]);

  const activeTokens = useMemo(() => {
    const tokens: Array<{ key: string; label: string; onRemove: () => void }> = [];

    if (q.trim()) {
      tokens.push({ key: "q", label: q.trim(), onRemove: () => setQ("") });
    }
    if (category) {
      tokens.push({ key: "category", label: category, onRemove: () => setCategory(null) });
    }
    if (color) {
      tokens.push({ key: "color", label: color, onRemove: () => setColor(null) });
    }
    if (size) {
      tokens.push({ key: "size", label: size, onRemove: () => setSize(null) });
    }
    if (inStockOnly) {
      tokens.push({ key: "stock", label: text.inStock, onRemove: () => setInStockOnly(false) });
    }

    return tokens;
  }, [category, color, inStockOnly, q, size, text.inStock]);

  const heroTitle = debouncedQ.trim()
    ? `${text.search} / ${debouncedQ.trim()}`
    : activeCollectionName || text.allPieces;

  const cardLabel = debouncedQ.trim()
    ? text.searchResult
    : activeCollectionName || text.currentCollection;

  const quickImage = resolveImg(
    (quickDetail as any)?.thumbnail ??
      (Array.isArray((quickDetail as any)?.images)
        ? (quickDetail as any).images?.[0]?.imageUrl
        : null) ??
      null,
  );

  const quickHasSale =
    quickDetail &&
    typeof quickDetail.salePrice === "number" &&
    quickDetail.salePrice > 0 &&
    quickDetail.salePrice < quickDetail.basePrice;

  return (
    <div className="min-h-screen bg-brand-ivory text-brand-brown">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#f7f0e5]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.08),transparent_22%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.05),transparent_30%)]" />
        <div className="absolute left-[-8%] top-[4%] h-[300px] w-[300px] rounded-full bg-brand-gold/8 blur-3xl" />
        <div className="absolute bottom-[-8%] right-[-6%] h-[280px] w-[280px] rounded-full bg-brand-brown/7 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="border-b border-brand-brown/10 pt-24 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 sm:pb-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-brown/10 bg-white/72 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/62 shadow-[0_12px_30px_rgba(43,31,26,0.04)]">
                <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
                {text.heroKicker}
              </div>

              <h1 className="mt-5 text-[34px] font-medium leading-[0.96] tracking-[0.01em] text-brand-brown sm:text-[48px] lg:text-[62px]">
                {heroTitle}
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-brand-brown/58 sm:text-[15px] sm:leading-8">
                {text.heroSubtitle}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/52">
                <span className="rounded-full border border-brand-brown/10 bg-white/70 px-4 py-2">
                  {resultCountLabel}
                </span>
                <span className="rounded-full border border-brand-brown/10 bg-white/70 px-4 py-2">
                  {String(activeFilterCount).padStart(2, "0")} {text.filters}
                </span>
                <span className="rounded-full border border-brand-brown/10 bg-white/70 px-4 py-2">
                  {text.localBrand}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 lg:max-w-[38%] lg:justify-end">
              {collections.slice(0, 8).map((c) => {
                const active = c.slug === activeCollectionSlug;
                return (
                  <FilterChip
                    key={c.slug}
                    active={active}
                    onClick={() => {
                      setActiveCollectionSlug(c.slug);
                      setQ("");
                    }}
                  >
                    {c.name}
                  </FilterChip>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="sticky top-0 z-30 border-b border-brand-brown/10 bg-brand-ivory/86 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <div className="grid gap-3 xl:grid-cols-[1fr_auto] xl:items-center">
            <div className="grid gap-3 md:grid-cols-[minmax(240px,1fr)_repeat(4,minmax(0,160px))]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-brown/38" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={text.searchPlaceholder}
                  className="h-11 w-full rounded-full border border-brand-brown/12 bg-white/82 pl-11 pr-12 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/78 outline-none transition placeholder:text-brand-brown/34 focus:border-brand-brown/35"
                />
                {q.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-brand-brown/45 transition hover:text-brand-brown"
                    aria-label={text.close}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <SelectField
                value={category ?? ""}
                onChange={(e) => setCategory(e.target.value ? e.target.value : null)}
              >
                <option value="">{text.category}</option>
                {(facets.categories ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </SelectField>

              <SelectField
                value={color ?? ""}
                onChange={(e) => setColor(e.target.value ? e.target.value : null)}
              >
                <option value="">{text.color}</option>
                {(facets.colors ?? []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </SelectField>

              <SelectField
                value={size ?? ""}
                onChange={(e) => setSize(e.target.value ? e.target.value : null)}
              >
                <option value="">{text.size}</option>
                {(facets.sizes ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </SelectField>

              <FilterChip active={inStockOnly} onClick={() => setInStockOnly((v) => !v)}>
                {text.inStock}
              </FilterChip>
            </div>

            <div className="flex flex-wrap items-center gap-3 xl:justify-end">
              <SelectField
                value={sort}
                onChange={(e) => setSort(e.target.value as any)}
                className="min-w-[132px]"
              >
                <option value="new">{text.newest}</option>
                <option value="old">{text.oldest}</option>
                <option value="priceAsc">{text.priceAsc}</option>
                <option value="priceDesc">{text.priceDesc}</option>
              </SelectField>

              <button
                type="button"
                onClick={() => setDense((v) => !v)}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/12 bg-white/78 px-5 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:border-brand-brown/24"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {dense ? text.comfort : text.dense}
              </button>

              <button
                type="button"
                onClick={() => {
                  setCategory(null);
                  setColor(null);
                  setSize(null);
                  setInStockOnly(false);
                  setQ("");
                }}
                className="inline-flex h-11 items-center gap-2 rounded-full border border-brand-brown/12 bg-white/78 px-5 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/72 transition hover:border-brand-brown/24"
              >
                <Filter className="h-4 w-4" />
                {text.reset}
              </button>
            </div>
          </div>

          {activeTokens.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {activeTokens.map((token) => (
                <button
                  key={token.key}
                  type="button"
                  onClick={token.onRemove}
                  className="inline-flex h-8 items-center gap-2 rounded-full border border-brand-brown/12 bg-white px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-brand-brown/70 transition hover:border-brand-brown/24"
                >
                  {token.label}
                  <X className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        {loading && (
          <div
            className={cn(
              "grid gap-5 sm:gap-6",
              dense
                ? "grid-cols-2 md:grid-cols-4"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4",
            )}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-[28px] border border-red-500/20 bg-red-500/5 p-8 text-center shadow-[0_20px_60px_rgba(220,38,38,0.08)]">
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState
            title={text.noProducts}
            desc={
              q.trim()
                ? text.noProductsDescSearch(q.trim())
                : text.noProductsDescCollection(activeCollectionName)
            }
            action={text.resetFilters}
            onReset={() => {
              setCategory(null);
              setColor(null);
              setSize(null);
              setInStockOnly(false);
              setQ("");
            }}
          />
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/45">
                  {text.productGrid}
                </p>
                <p className="mt-1 text-sm text-brand-brown/62">{resultCountLabel}</p>
              </div>

              {inStockOnly && (
                <div className="inline-flex h-9 items-center gap-2 rounded-full bg-[#231a18] px-4 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-ivory">
                  <Check className="h-3.5 w-3.5" />
                  {text.inStock}
                </div>
              )}
            </div>

            <div
              className={cn(
                "grid gap-x-5 gap-y-8 sm:gap-x-6 sm:gap-y-10",
                dense
                  ? "grid-cols-2 md:grid-cols-4"
                  : "grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4",
              )}
            >
              {items.map((p) => (
                <ProductCard
                  key={Number((p as any).id ?? 0)}
                  p={p}
                  label={cardLabel}
                  dense={dense}
                  desktop={desktop}
                  text={text}
                  go={go}
                  openQuickFor={openQuickFor}
                />
              ))}
            </div>
          </>
        )}

        <div ref={sentinelRef} />

        {loadingMore && (
          <div className="mt-10 flex items-center justify-center">
            <div className="inline-flex h-11 items-center rounded-full border border-brand-brown/12 bg-white px-5 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/55">
              {text.loadingMore}
            </div>
          </div>
        )}
      </section>

      {/* QUICK VIEW */}
      {openQuickId != null && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/65 backdrop-blur-sm"
            onClick={closeQuick}
          />

          <div className="absolute inset-0 overflow-y-auto">
            <div className="min-h-full px-0 md:grid md:place-items-center md:p-6">
              <div className="mt-auto w-full overflow-hidden rounded-t-[30px] border border-brand-brown/15 bg-brand-ivory shadow-[0_60px_180px_rgba(0,0,0,0.35)] md:mt-0 md:w-[1080px] md:rounded-[34px]">
                <div className="flex items-center justify-between border-b border-brand-brown/10 p-5 sm:p-6">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                      {text.quickView}
                    </p>
                    <h3 className="mt-2 text-[20px] font-medium text-brand-brown sm:text-[24px]">
                      {quickDetail?.name || "—"}
                    </h3>
                  </div>

                  <button
                    type="button"
                    onClick={closeQuick}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-brown/10 bg-white text-brand-brown/68 transition hover:border-brand-brown/20 hover:text-brand-brown"
                    aria-label={text.close}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid gap-0 md:grid-cols-[0.95fr_1.05fr]">
                  <div className="border-b border-brand-brown/10 bg-white/50 md:border-b-0 md:border-r">
                    <div className="aspect-[4/5] bg-[#efe8de]">
                      {quickLoading ? (
                        <div className="h-full w-full animate-pulse bg-brand-brown/6" />
                      ) : (
                        <SafeImg
                          src={quickImage}
                          alt={quickDetail?.name ?? ""}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  </div>

                  <div className="p-5 sm:p-6 lg:p-8">
                    {quickLoading || !quickDetail ? (
                      <div className="space-y-4">
                        <div className="h-4 w-28 animate-pulse rounded bg-brand-brown/10" />
                        <div className="h-8 w-2/3 animate-pulse rounded bg-brand-brown/10" />
                        <div className="h-12 w-full animate-pulse rounded bg-brand-brown/10" />
                        <div className="h-12 w-full animate-pulse rounded bg-brand-brown/10" />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            {/* <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/42">
                              {text.brand}
                            </p> */}
                            <h4 className="mt-3 text-[24px] font-medium leading-[1.02] text-brand-brown">
                              {quickDetail.name}
                            </h4>
                          </div>

                          <div className="text-right">
                            {quickHasSale && (
                              <p className="text-sm text-brand-brown/36 line-through">
                                {formatVND(quickDetail.basePrice)}
                              </p>
                            )}
                            <p className="text-[22px] font-medium text-brand-brown">
                              {formatVND(quickPrice)}
                            </p>
                          </div>
                        </div>

                        <div className="mt-7 space-y-6">
                          <div>
                            <div className="flex items-center justify-between gap-4">
                              <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
                                {text.color}
                              </p>
                              {qColor && (
                                <p className="text-xs text-brand-brown/55">{qColor}</p>
                              )}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {quickColors.map((c) => (
                                <FilterChip
                                  key={c}
                                  active={c === qColor}
                                  onClick={() => {
                                    setQColor(c);
                                    setQSize(null);
                                    setQQty(1);
                                  }}
                                >
                                  {c}
                                </FilterChip>
                              ))}

                              {quickColors.length === 0 && (
                                <p className="text-sm text-brand-brown/55">
                                  {text.noStockVariants}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center justify-between gap-4">
                              <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
                                {text.size}
                              </p>
                              {qSize && (
                                <p className="text-xs text-brand-brown/55">{qSize}</p>
                              )}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {!qColor ? (
                                <p className="text-sm text-brand-brown/55">
                                  {text.chooseColorFirst}
                                </p>
                              ) : quickSizes.length === 0 ? (
                                <p className="text-sm text-brand-brown/55">
                                  {text.noSize}
                                </p>
                              ) : (
                                quickSizes.map((s) => (
                                  <FilterChip
                                    key={s}
                                    active={s === qSize}
                                    onClick={() => setQSize(s)}
                                  >
                                    {s}
                                  </FilterChip>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                            <div>
                              <div className="flex items-center justify-between gap-4">
                                <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/46">
                                  {text.quantity}
                                </p>

                                {typeof quickStock === "number" && (
                                  <p className="text-xs text-brand-brown/55">
                                    {text.stock}: {quickStock}
                                  </p>
                                )}
                              </div>

                              <div className="mt-3 inline-flex items-center overflow-hidden rounded-full border border-brand-brown/15">
                                <button
                                  type="button"
                                  onClick={() => setQQty((x) => Math.max(1, x - 1))}
                                  className="inline-flex h-10 w-12 items-center justify-center transition hover:bg-brand-brown/5"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>

                                <div className="grid h-10 w-12 place-items-center text-sm">
                                  {qQty}
                                </div>

                                <button
                                  type="button"
                                  onClick={() =>
                                    setQQty((x) =>
                                      typeof quickStock === "number" && quickStock > 0
                                        ? Math.min(quickStock, x + 1)
                                        : x + 1,
                                    )
                                  }
                                  className="inline-flex h-10 w-12 items-center justify-center transition hover:bg-brand-brown/5"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                            <div className="rounded-[20px] border border-brand-brown/10 bg-white px-5 py-4 shadow-[0_10px_34px_rgba(43,31,26,0.04)]">
                              <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/42">
                                {text.selected}
                              </p>
                              <p className="mt-2 text-sm font-medium text-brand-brown">
                                {qColor && qSize ? `${qColor} / ${qSize}` : "—"}
                              </p>
                            </div>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => {
                                closeQuick();
                                go(`/product/${openQuickId}`);
                              }}
                              className="inline-flex h-12 items-center justify-center rounded-full border border-brand-brown text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:bg-brand-brown hover:text-brand-ivory"
                            >
                              {text.fullPage}
                            </button>

                            <button
                              type="button"
                              disabled={!quickVariant || addingQuick}
                              onClick={handleQuickAdd}
                              className="inline-flex h-12 items-center justify-center rounded-full bg-[#1f1715] text-[11px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-90 disabled:opacity-35"
                            >
                              {addingQuick ? text.adding : text.addToCart}
                            </button>
                          </div>

                          {!quickVariant && (
                            <p className="text-sm text-brand-brown/55">
                              {text.chooseVariant}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="h-8 sm:h-10" />
    </div>
  );
}